/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/phone/QaPhoneRunList.tsx
 * FILE_ID: JOBHUB-QA-PHONE-RUN-001
 * PURPOSE: Phone Run body. Reuses AreaExecutionPanel so filters, verify, photos, notes stay.
 */
import type { QaAreaExecution, QaFloor } from '../../../qa/qaApi'
import { checklistGroups, normalizeList } from '../../../qa/qaApiHelpers'
import { AreaExecutionPanel } from '../../JobQaExecution'
import { groupNameOf } from '../qaTabSelectors'
import type { JobQaTabController } from '../useJobQaTabController'
import { QA_PHONE_CONTROLS } from './qaPhoneControlInventory'

export function QaPhoneRunList({
  controller,
  onOpenItp,
}: {
  controller: JobQaTabController
  onOpenItp: (checklistIds: string[]) => void
}) {
  const { api, props, selection, workspace } = controller
  if (workspace.inspections.length === 0) {
    return <p className="p-4 text-sm text-muted-foreground">No QA on this job yet</p>
  }
  const execution = workspace.areaExecQuery?.data
  if (!execution) {
    return <p className="p-4 text-sm text-muted-foreground">Select an area to run its checklist.</p>
  }
  const checklistId = workspace.allChecklists ? null : workspace.activeChecklistId
  const areas = normalizeList<QaAreaExecution>(workspace.areasQuery.data)
  const floors = normalizeList<QaFloor>(workspace.floorsQuery.data)
  const groups = checklistGroups(execution)
  return (
    <div
      data-qa-phone-control={[
        QA_PHONE_CONTROLS.verifyChecklist,
        QA_PHONE_CONTROLS.verifyCheck,
        QA_PHONE_CONTROLS.filterPills,
        QA_PHONE_CONTROLS.statusChip,
        QA_PHONE_CONTROLS.photo,
        QA_PHONE_CONTROLS.attach,
        QA_PHONE_CONTROLS.requirements,
        QA_PHONE_CONTROLS.criteria,
        QA_PHONE_CONTROLS.notes,
        QA_PHONE_CONTROLS.photoGallery,
        QA_PHONE_CONTROLS.evidence,
        QA_PHONE_CONTROLS.notesThread,
        QA_PHONE_CONTROLS.reading,
        QA_PHONE_CONTROLS.changeBanner,
        QA_PHONE_CONTROLS.hold,
        QA_PHONE_CONTROLS.parked,
        QA_PHONE_CONTROLS.signoff,
        QA_PHONE_CONTROLS.sectionCollapse,
        QA_PHONE_CONTROLS.openItp,
      ].join(' ')}
    >
      <AreaExecutionPanel
        area={execution}
        checklistId={checklistId}
        hideTitleRow
        areas={areas.map((row) => ({
          id: row.id,
          name: row.area_name,
          group: groupNameOf(row, floors),
        }))}
        checklists={groups.map((group) => ({ id: group.checklist.id, name: group.name }))}
        onSelectArea={(areaId) => workspace.selectArea(areaId)}
        onSelectChecklist={(id) => {
          // Reused from: packages/job-hub/src/jobs/tabs/job-qa/JobQaAreaDetail.tsx (AreaExecutionPanel)
          workspace.setAllChecklists(false)
          workspace.setActiveChecklistId(id)
        }}
        jobId={props.jobId}
        api={api}
        onMutated={workspace.invalidateExecution}
        onSetStatus={workspace.setItemStatus}
        onSetAreaStatus={(areaId, status) => workspace.setAreaWorkStatus.mutate({ areaId, status })}
        statusPending={workspace.setStatus.isPending || workspace.setAreaWorkStatus.isPending}
        onOpenItp={onOpenItp}
        onWriteError={controller.setError}
        itemSelection={{
          isSelected: (id, checklistId) => selection.isCheckIncluded(id, checklistId, execution.id),
          onToggle: (id, checklistId) => selection.toggleCheck(id, checklistId, execution.id),
        }}
        checklistSelection={{
          isSelected: (id) => selection.isSelected('checklists', id),
          onToggle: (id) => selection.toggle('checklists', id),
        }}
        onRenameChecklist={execution.status === 'locked' ? undefined : (checklistId, title) => {
          void api.renameChecklist(checklistId, title).then(workspace.invalidateExecution)
        }}
      />
    </div>
  )
}
